import { Avatar, AvatarSize } from "react-migration/components/Avatar";
import {
  ContextMenu,
  ContextMenuAlignAlignment,
  MenuItemProps,
  MenuItemType,
  MenuItemVariant,
} from "react-migration/components/ContextMenu";
import { MeasurementSystem, userStore } from "src/js/stores/user/store";
import { ReactNode, useMemo } from "react";
import { handleUpgrade, shouldShowUpgrade } from "react-migration/components/UpgradeButton";
import {
  openAddOnsStoreModal,
  setDebugModal,
  setShowTopupModal,
  setUserColorModal,
} from "src/js/stores/navigation/actions";
import { router, useNavigate } from "src/js/router/react-router";

import { ENVIRONMENT } from "src/js/util/environment";
import Feature from "src/js/stores/user/Feature";
import { Icon } from "react-migration/components/Icon";
import { Intercom } from "react-migration/lib/util/intercom";
import ROUTES from "src/js/router/routes";
import { UserMenuMobile } from "./UserMenuMobile";
import { formatToCurrency } from "react-migration/lib/util/numberFormat";
import hasBetaFeature from "src/js/stores/user/actions/hasBetaFeature";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import { isVue } from "react-migration/lib/util/isVue";
import { logEvent } from "react-migration/lib/util/logEvent";
import { refreshUser } from "src/js/util/user";
import saveUser from "src/js/stores/user/actions/saveUser";
import { setUnitPreference } from "src/js/stores/drawing/actions/setUnitPreference";
import { updateMeasurementSystemPreference } from "src/js/stores/user/actions/updateSetting";
// TODO: Don't reference a domain from another domain
import { useAccountQuery } from "react-migration/domains/dashboard/topup/hooks/useAccountQuery";
import { useIsMobile } from "react-migration/lib/theme/hooks/useIsMobile";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

export const UserMenu = ({ triggerElement }: { triggerElement?: ReactNode }) => {
  const { t } = useTranslation();
  const {
    user: {
      firstname,
      surname,
      plan_name,
      settings: { unit },
    },
  } = useSnapshot(userStore);
  const { data: accountData } = useAccountQuery();
  const views = accountData?.accountWithViews;
  const isUsUser = hasFeature(Feature.usAccess);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const hasUnlimitedOwnershipClicks = hasFeature(Feature.unlimitedOwnershipClicks);
  const hasUnlimitedPlanningClicks = hasFeature(Feature.unlimitedPlanningClicks);
  const credits = accountData?.accountWithViews?.land_reg_credit_pounds || 0;

  const creditsKeysToDisplayUK = ["ownership_views", "planning_views"];
  const creditsKeysToDisplayUS = ["skip_traces", "included_letter_send"];
  const creditsKeysToDisplay = isUsUser ? creditsKeysToDisplayUS : creditsKeysToDisplayUK;

  const unlimitedMap: Record<string, string> = {
    planning_views: hasUnlimitedPlanningClicks ? "planning_views.unlimited" : "planning_views",
    ownership_views: hasUnlimitedOwnershipClicks ? "ownership_clicks.unlimited" : "ownership_views",
    skip_traces: "skip_traces",
    included_letter_send: "included_letter_send",
  };

  const openTopupModal = () => {
    setShowTopupModal(true);
    logEvent("Top Up Started", { location: "modal" });
  };

  const openDebugModal = () => {
    setDebugModal(true);
    logEvent("Open Debug Modal");
  };

  const openUserColorModal = () => {
    setUserColorModal(true);
    logEvent("Open User Color Modal");
  };

  const openBilling = () => {
    logEvent("Anchor Link Selected", { link: "customer-portal" });
    window.location.replace(`${ENVIRONMENT.ACCOUNTS_SERVICE_PRIVATE_API_URL}/customer-portal`);
  };

  const separator = {
    type: MenuItemType.SEPARATOR,
  };

  const yourAccountItem = {
    type: MenuItemType.ITEM,
    testId: "your-account",
    label: `${firstname} ${surname}`,
    subLabel: <span className="text-xs capitalize text-text-base-secondary-a">{`${plan_name} Plan`}</span>,
    leadElement: (
      <Avatar firstName={firstname ?? ""} lastName={surname ?? ""} size={AvatarSize.MD} className="-ml-1.5" />
    ),
    tailElement: <Icon icon="icon-lt-user-line" className="ml-auto self-center text-text-base-secondary-a" />,

    onClick: () => {
      openUserColorModal();
    },
  };

  const upgradeItem = {
    type: MenuItemType.ITEM,
    label: t("nav.nav.upgrade"),
    leadElement: <Icon icon="icon-lt-arrow-up-circle-line" />,
    onClick: () => {
      handleUpgrade({ source: "UserMenu" });
    },
  };

  const settingsItem = {
    type: MenuItemType.ITEM,
    label: t("nav.nav.settings"),
    leadElement: <Icon icon="icon-lt-settings-2-line" />,
    onClick: () => {
      isVue() ? router.push({ name: ROUTES.DASHBOARD }) : navigate(ROUTES.DASHBOARD);
    },
  };

  const addonsItem = {
    type: MenuItemType.ITEM,
    label: t("nav.nav.addons"),
    leadElement: <Icon icon="icon-lt-box-3-line" />,
    onClick: () => {
      openAddOnsStoreModal({
        logClickSource: "user-menu",
      });
    },
  };

  const unitsItem = {
    type: MenuItemType.ITEM,
    label: unit === MeasurementSystem.METRIC ? t("units.system.metric") : t("units.system.imperial"),
    leadElement: <Icon icon="icon-lt-ruler-2-line" />,
    onClick: () => {
      updateMeasurementSystemPreference(
        unit === MeasurementSystem.METRIC ? MeasurementSystem.IMPERIAL : MeasurementSystem.METRIC
      );
      saveUser();
      setUnitPreference(unit === MeasurementSystem.METRIC ? MeasurementSystem.IMPERIAL : MeasurementSystem.METRIC);
      logEvent("Toggle Units", {
        unitType: unit === MeasurementSystem.METRIC ? MeasurementSystem.IMPERIAL : MeasurementSystem.METRIC,
      });
    },
  };

  const billingItem = {
    type: MenuItemType.ITEM,
    label: t("nav.nav.billing"),
    leadElement: <Icon icon="icon-lt-receipt-line" />,
    onClick: () => {
      logEvent("Anchor Link Selected", { link: "customer-portal" });
      openBilling();
    },
  };

  const balanceItem = {
    type: MenuItemType.ITEM,
    label: `${t("nav.nav.balance")}: ${credits < 0 ? "-" : ""}${formatToCurrency({
      amount: credits,
      currency: isUsUser ? "USD" : "GBP",
    })}`,
    testId: "balance-element",
    leadElement: <Icon icon={isUsUser ? "icon-lt-money-dollar-circle-line" : "icon-lt-money-pound-circle-line"} />,
    onClick: () => {
      isMobile ? router.push({ name: ROUTES.DASHBOARD_TOPUP }) : openTopupModal();
    },
  };

  const creditsItem = {
    type: MenuItemType.ITEM,
    label: t("nav.nav.credits", {
      type: hasUnlimitedOwnershipClicks ? "Unlimited" : "Limited",
    }),
    leadElement: <Icon icon="icon-lt-coupon-ticket" extraClasses="self-start" />,
    onClick: () => {
      router.push({ name: ROUTES.DASHBOARD_TOPUP });
    },
    subLabel: (
      <div className="flex flex-col w-full gap-none text-text-base-secondary-a">
        {views &&
          Object.entries(views)
            .filter(([key]) => creditsKeysToDisplay.includes(key))
            .map(([key, value]) => {
              if ((value.cap || value.cap === 0) && (value.used || value.used === 0)) {
                const displayValue = isUsUser ? value.cap - value.used : value.used;
                return (
                  <span className="text-xs text-text-base-secondary-a" key={key}>
                    {t(`nav.nav.credits.${unlimitedMap[key]}`, { value: displayValue, cap: value.cap })}
                  </span>
                );
              }
            })}
      </div>
    ),
  };

  const supportItem = {
    type: MenuItemType.ITEM,
    testId: "support-element",
    label: t("nav.nav.support"),
    subLabel: (
      <span className="text-sm capitalize text-text-base-secondary-a">
        {hasFeature(Feature.usAccess) ? "calum@land.tech" : "0203 086 7855"}
      </span>
    ),
    leadElement: <Icon icon="icon-lt-customer-service-2-line" />,
  };

  const chatItem = {
    type: MenuItemType.ITEM,
    testId: "chat-element",
    label: t("nav.nav.chat"),
    leadElement: <Icon icon="icon-lt-intercom-line" />,
    onClick: () => {
      Intercom("show");
    },
  };

  const debugItem = {
    type: MenuItemType.ITEM,
    testId: "debug-element",
    label: t("nav.nav.debug"),
    leadElement: <Icon icon="icon-lt-bug-line" />,
    onClick: () => {
      openDebugModal();
    },
  };

  const logoutItem = {
    type: MenuItemType.ITEM,
    testId: "logout-element",
    label: t("nav.nav.logout"),
    variant: MenuItemVariant.DESTRUCTIVE,
    leadElement: <Icon icon="icon-lt-logout-line" />,
    onClick: () => {
      window.location.href = ENVIRONMENT.LOGOUT_URL;
    },
  };

  const refreshUserItem = {
    type: MenuItemType.ITEM,
    testId: "refresh-user",
    label: "Refresh User",
    leadElement: <Icon icon="icon-lt-user-line" />,
    onClick: () => {
      refreshUser();
    },
  };

  const menuConfig = [
    yourAccountItem,
    shouldShowUpgrade("UserMenu") ? upgradeItem : null,
    billingItem,
    isUsUser ? null : balanceItem,
    creditsItem,
    separator,
    isUsUser ? null : addonsItem,
    unitsItem,
    settingsItem,
    separator,
    supportItem,
    chatItem,
    ENVIRONMENT.APP_ENV === "local" || hasBetaFeature(Feature.debugMenu) ? debugItem : null,
    separator,
    logoutItem,
    ENVIRONMENT.APP_ENV === "local" ? refreshUserItem : null,
  ].filter(Boolean) as MenuItemProps[];

  const trigger = useMemo(() => {
    return triggerElement ? (
      triggerElement
    ) : (
      <Icon icon="icon-lt-more-2-line" size="lg" extraClasses="block text-xl md:hidden pt-2" cursor="pointer" />
    );
  }, [triggerElement]);

  if (isMobile) {
    return <UserMenuMobile menuItems={menuConfig} trigger={trigger} />;
  }

  return (
    <ContextMenu
      menuItems={menuConfig}
      trigger={trigger}
      triggerIconProps={{
        icon: "icon-lt-more-2-line",
        size: "lg",
        extraClasses: "block text-xl",
        cursor: "pointer",
      }}
      triggerTestId="usermenu-trigger-element"
      align={ContextMenuAlignAlignment.START}
      noBorder={true}
    />
  );
};
